// Desktop Create Organization Side Panel - Refactored to use shared hook
import React, { useEffect } from 'react';
import { View, Text, TextInput, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { ADMIN_COLORS } from '@/constants/admin';
import { useOrganizationForm } from '@/hooks/admin/useOrganizationForm';
import { EntrataUsersInput } from './EntrataUsersInput';
import { organizationFormStyles as styles } from './organizationFormStyles';

interface DesktopCreateOrganizationPanelProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const DesktopCreateOrganizationPanel: React.FC<DesktopCreateOrganizationPanelProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const {
    formData,
    errors,
    isLoading,
    isPatchingConfig,
    updateField,
    updatePaymentAccount,
    resetForm,
    createOrganization,
  } = useOrganizationForm({ onSuccess, onClose });

  // Reset form when panel closes
  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <DesktopSidePanel
      visible={visible}
      onClose={handleClose}
      width={520}
    >
      <DesktopPanelHeader
        title="New Organization"
        onClose={handleClose}
      />

      <DesktopPanelBody>
        <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
          {/* About Organization Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>About Organization</Text>

            <View style={styles.field}>
              <Text style={styles.label}>Organization Name *</Text>
              <TextInput
                style={[styles.input, errors.name && styles.inputError]}
                value={formData.name}
                onChangeText={(text) => updateField('name', text)}
                placeholder="Organization name"
                placeholderTextColor={ADMIN_COLORS.TEXT.PRIMARY}
              />
              {errors.name ? <Text style={styles.errorText}>{errors.name}</Text> : null}
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>Organization Domain *</Text>
              <TextInput
                style={[styles.input, errors.domain && styles.inputError]}
                value={formData.domain}
                onChangeText={(text) => updateField('domain', text.toLowerCase())}
                placeholder="organization-domain"
                autoCapitalize="none"
                placeholderTextColor={ADMIN_COLORS.TEXT.PRIMARY}
              />
              {errors.domain ? <Text style={styles.errorText}>{errors.domain}</Text> : null}
            </View>
          </View>

          {/* Entrata API Users Section */}
          <View style={styles.section}>
            <EntrataUsersInput
              users={formData.entrataUsers}
              onChange={(users) => updateField('entrataUsers', users)}
              disabled={isLoading || isPatchingConfig}
            />
          </View>

          {/* Payment Accounts Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Payment Accounts</Text>

            {formData.paymentAccounts.map((account, index) => (
              <View key={account.name} style={styles.paymentAccountField}>
                <Text style={styles.label}>{account.description}</Text>
                <TextInput
                  style={styles.input}
                  value={account.accountNumber?.toString() || ''}
                  onChangeText={(text) => updatePaymentAccount(index, text)}
                  placeholder=""
                  keyboardType="numeric"
                  placeholderTextColor={ADMIN_COLORS.TEXT.PRIMARY}
                />
              </View>
            ))}
          </View>

          {/* Actions */}
          <View style={styles.actionsContainer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={handleClose}
              disabled={isLoading}
              activeOpacity={0.7}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button,
                styles.createButton,
                (isLoading || isPatchingConfig) && styles.disabledButton,
              ]}
              onPress={createOrganization}
              disabled={isLoading || isPatchingConfig}
              activeOpacity={0.7}
            >
              {(isLoading || isPatchingConfig) ? (
                <ActivityIndicator color="#fff" size="small" />
              ) : (
                <Text style={styles.createButtonText}>Create Organization</Text>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </DesktopPanelBody>
    </DesktopSidePanel>
  );
};
